import { useState } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/Reveal";
import { locations } from "@/data/content";

// Approximate positions of the towns within a light, minimal map panel
const markers = [
  { name: "Plzeň", x: 56, y: 42, main: true },
  { name: "Rokycany", x: 78, y: 40 },
  { name: "Klatovy", x: 52, y: 82 },
  { name: "Domažlice", x: 26, y: 74 },
  { name: "Tachov", x: 14, y: 22 },
  { name: "Stříbro", x: 30, y: 36 },
  { name: "Dobřany", x: 48, y: 56 },
];

export const Coverage = () => {
  const [value, setValue] = useState("");
  const [result, setResult] = useState("");

  const check = () => {
    if (!value.trim()) return;
    setResult("Lokalitu s Vámi ověříme při úvodní domluvě.");
  };

  return (
    <section className="bg-card" data-testid="coverage-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Oblast působnosti
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Působíme v Plzni a širokém okolí
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
              Nejčastěji realizujeme zakázky do 50 km od Plzně. Větší projekty
              po domluvě i mimo tento okruh. Napište nám svou obec a domluvíme
              se na dalším postupu.
            </p>
            <div className="mt-8 flex max-w-md flex-col gap-3 sm:flex-row">
              <Input
                data-testid="coverage-input"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Vaše obec, např. Dobřany"
                className="h-12 border-input bg-background"
                onKeyDown={(e) => e.key === "Enter" && check()}
              />
              <Button
                data-testid="coverage-submit"
                onClick={check}
                className="h-12 shrink-0 bg-primary px-6 text-primary-foreground hover:bg-primary/90"
              >
                Ověřit zakázku v mé lokalitě
              </Button>
            </div>
            {result && (
              <p data-testid="coverage-result" className="mt-4 flex items-center gap-2 text-sm font-medium text-accent">
                <MapPin className="h-4 w-4" /> {result}
              </p>
            )}
            <div className="mt-8 flex flex-wrap gap-2">
              {locations.map((l) => (
                <span
                  key={l}
                  className="rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-medium text-muted-foreground"
                >
                  {l}
                </span>
              ))}
            </div>
          </Reveal>

          {/* Minimal light map */}
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md border border-border bg-background">
              <svg viewBox="0 0 100 75" className="absolute inset-0 h-full w-full" aria-hidden="true">
                {/* soft terrain shapes */}
                <path d="M-5 60 Q 20 48 38 56 T 70 58 T 108 52 V 80 H -5 Z" fill="hsl(42 31% 94%)" />
                <path d="M-5 30 Q 18 22 34 27 T 68 24 T 108 28 V -5 H -5 Z" fill="hsl(42 31% 95%)" />
                <path d="M30 -5 Q 42 25 56 31.5 Q 72 38 80 75" fill="none" stroke="hsl(42 11% 86%)" strokeWidth="0.6" strokeDasharray="1.6 1.6" />
                <path d="M-5 40 Q 25 36 56 31.5 Q 80 28 105 34" fill="none" stroke="hsl(42 11% 86%)" strokeWidth="0.6" strokeDasharray="1.6 1.6" />
              </svg>
              {markers.map((m) => (
                <div
                  key={m.name}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${m.x}%`, top: `${m.y}%` }}
                >
                  <span className="flex flex-col items-center">
                    <span
                      className={`block rounded-full ${
                        m.main
                          ? "h-4 w-4 border-4 border-accent/30 bg-accent"
                          : "h-2.5 w-2.5 bg-primary/70"
                      }`}
                    />
                    <span
                      className={`mt-1.5 whitespace-nowrap text-xs ${
                        m.main ? "font-bold text-foreground" : "font-medium text-muted-foreground"
                      }`}
                    >
                      {m.name}
                    </span>
                  </span>
                </div>
              ))}
              <span className="absolute bottom-3 right-4 text-[10px] uppercase tracking-widest text-muted-foreground/60">
                Plzeňský kraj
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
